import * as THREE from 'three';
import { DEPTH_ZONES, elevationM, summarize, type Seafloor } from '../sim/seafloor';
import { binFloorsKm2, type Reef } from '../sim/reef';
import { CORAL_RATES } from '../sim/coral';
import { depthAtShare, depthAxis, depthShare, landShare, placeRulerLabels, stretches, type DepthAxis } from './depthAxis';
import { lightShare } from '../sim/light';
import { SKIN_TILE_PX } from './skinTiles';
import { attachSkin, ownTile, type CausticUniforms, type FogUniforms, type OwnTile, type SkinUniforms } from './skin';
import { RIPPLE_TILE } from './data/ripple';
import { SEAFLOOR_TILE } from './data/sand';
import { glowAt, sunDirection, yearOfNoons, type DaySun } from './sunlight';
import { FishSchools, type KindCounts } from './fishSchools';
import { FISH_PER_SPRITE } from './fishSwim';
import { FISH_KINDS } from './fishKinds';
import { TagMarks, schoolMark, type MarkTarget } from './tagMarks';
import { ReefOutcrops, outcropPlaces, type ReefModel, type ReefTextures } from './reefOutcrops';

/**
 * One sanctuary drawn as an aquarium: a glass box of water over its real
 * seafloor (M17).
 *
 * **Depth is drawn stretched, and says so** (M4, `GDD.md` section 12). The box
 * is a cube: its side is the aquarium's 100 km and its height the deepest
 * water in it. The top 1,000 m, where the life is, fill three quarters of the
 * height and the rest the last quarter (`depthAxis.ts`, the user's call); land
 * rises at the deep scale. Every scale is printed on the box beside a
 * true-depth ruler, and the seafloor is cut away at the walls down to the
 * box's floor, as a cutaway is, so the sections show.
 *
 * A sanctuary whose seafloor has not been built yet (Vava'u) is drawn as
 * Phase 0 drew every aquarium: the frame and a flat floor, the box's height
 * not a depth.
 *
 * **The noon sun** stands over the box on the meridian, on the year's path
 * drawn beside it, and lights the box from there; the water glows down to
 * where the day's light reaches (M34, `sunlight.ts`; `setDay`).
 *
 * **The reef** (M47, `setReef`): the columns with floor to 30 m are tinted
 * toward live coral's color by their cover and whiten as it bleaches, each
 * by its own depths' coral (M49), so a bleached reef and a dead one look
 * different from a living one at a glance (`GDD.md` section 12).
 *
 * **The skins** (M77, `skin.ts`): pixel-art tiles of the user's sand, coral
 * heads, scrub and stacked lava laid over those colors, each divided by its own
 * mean so the colors read as they did.
 */

/** Scene units across one side of the aquarium. */
const SIDE = 12;
/** Scene units from the surface to the deepest floor: a cube. */
const HEIGHT = 12;
/** The surface, in scene units. */
const TOP = HEIGHT / 2;
const BOTTOM = -HEIGHT / 2;

/**
 * Colors by elevation, m: the seafloor from sand in the shallows to dark
 * basalt in the deep, the land from beach to the mountains' green and brown. A
 * reading aid, not a habitat map; the reef and its coral are tinted over it
 * (`setReef`).
 */
/**
 * The noon sun's path: a half circle over the surface's middle, its radius
 * two fifths of the box's side (JUDGED: clear of Maui Nui's mountains by 3
 * units at its highest, and the camera's fitted sphere hardly grows for it).
 * MEASURED, the sphere at Maui Nui: 11.40 units with the sun, 11.32 without,
 * so the aquarium is drawn 0.7% smaller; a circle reaching the rim made it
 * 12.60 (10% smaller), and the first version, a unit wider with a unit's
 * margin, 14.00 (19%).
 */
const SUN_PATH_RADIUS = SIDE * 0.4;
/** The sun's disc, CSS px across, at any distance (as the labels are). */
const SUN_PX = 34;
/** Scene units the fitted sphere keeps clear around the sun's center, for its disc and its label (JUDGED). */
const SUN_MARGIN = 0.6;
/** The key light at full sunlight: the strength the aquarium was lit with before the sun moved (JUDGED, the look of M4). */
const KEY_AT_FULL_SUN = 1.4;
/** Rows the water is drawn in: about 4 m apart above the break at Maui Nui, so the glow's fall over its first 100 m is smooth (JUDGED). */
const WATER_ROWS = 300;
/** The water's colors: unlit near the top and at the floor, and lit by the sun (JUDGED, a reading aid). */
const UNLIT_TOP = 0x0f3f5f;
const UNLIT_BOTTOM = 0x061624;
const LIT = 0x52b6e4;
/** How opaque the water is drawn, and how much more its walls are where it is fully lit (JUDGED: the seafloor shows through, the lit band reads). */
const WATER_OPACITY = 0.32;
const LIT_OPACITY = 0.3;

/**
 * The reef's tints: live coral's warm brown and bleached coral's white; how
 * far a column whose floor is all reef goes toward them, and how much of that
 * it shows with no live coral left (JUDGED, a reading aid: the reef reads as
 * a band along the shores, browner with more coral and white as it bleaches).
 */
export const LIVE_CORAL = new THREE.Color(0xc2683a);
export const BLEACHED_CORAL = new THREE.Color(0xffffff);
const FULL_REEF_TINT = 0.95;
const BARE_REEF_TINT = 0.3;
/** A column's reef floor, km², drawn as all reef: half its square kilometer (JUDGED: the 1 km columns along a shore are part land, part deep water). */
const FULL_REEF_KM2 = 0.5;
/** How wide the map's widest reef outcrop is drawn, scene units, the rest to its scale (M87, JUDGED: about 3 km, readable at the opening view; the exhibit's in `EXHIBIT_SPRITES`). */
const OUTCROP_UNITS = 0.35;

/**
 * Scene units a skin's texel spans: a 512th of the box's side, so a 64-pixel
 * tile repeats eight times across it. At Maui Nui a texel is 195 m, finer
 * than the seafloor's 1 km columns: the pattern is texture, not data. The
 * user's choice (M77: *"the pixels are way too big. make them 1/8 the
 * current size"*, from the first cut's 64th).
 */
export const SKIN_TEXEL = SIDE / 512;

/**
 * How fast the ripple drifts across the surface, scene units a second of the
 * player's time: 3.2 texels a second, JUDGED to read as a slow swell, not a
 * current, the user's word (M77: *"make the ripple drift"*). With the user's
 * own picture (`RIPPLE_TILE`, 4.27 light crests down a column of its 112
 * rows, MEASURED: runs of its three lightest colors), a crest passes about
 * every eight seconds; M77's drawn tile had five crests to 64 rows, one every
 * four. On the player's clock, not the game's: at 128× a game-time ripple
 * would be a blur, and it keeps drifting while paused, as water does.
 */
export const RIPPLE_DRIFT_PER_S = SIDE / 160;

/**
 * The caustics (the user's word, M78: *"do 2"*, the caustics and fog GDD
 * section 12 asks for): how far the floor is lifted where the two webs'
 * brightest lines cross and all the sun's light reaches, the share falling
 * with depth as the measured light does (`lightShare`). The webs are laid on
 * as their mean, so one web's line lifts it about half that (30% on average
 * over the other web's shades, 23% over its darkest), and where both show
 * their darkest shade it is dimmed 13% (MEASURED, M78's reviewer: the tile's
 * linear luma 0.0685 darkest, 0.2369 its mean; the lift over every pair of
 * the webs' offsets averages exactly none). JUDGED to read as sunlight
 * dancing on the shallows.
 */
export const CAUSTIC_CONTRAST = 0.6;
/**
 * How fast each copy of the caustic web slides, scene units a second of the
 * player's time, and which way: the two cross, so the web shimmers rather
 * than travels, faster than the swell above it (JUDGED, a look; real
 * caustics move with waves far smaller than a texel). Slid smoothly, its
 * texels blended (M227, the user's word: "caustics dont look smooth.").
 */
export const CAUSTIC_DRIFT_PER_S = SIDE / 120;
export const CAUSTIC_DIRECTIONS: readonly [readonly [number, number], readonly [number, number]] = [[0.8, 0.6], [-0.6, 0.8]];
/**
 * The fog, per scene unit of water the eye looks through: 30% of the way to
 * the water's color through 6 units, half the box's width
 * (1 - exp(-0.06 × 6)), 51% through its whole width; JUDGED, so the deep
 * floor fades into the water and the shallows seen from above stay clear. A
 * stand-in for the light the water scatters (GDD section 12: "fog in place of
 * volumetrics"); true to scale it would hide everything, the light at Maui Nui
 * falling to 37% over its first 12 m and to 1% by 64 m (its clarity, kdPar
 * 0.082 a meter on the opening day, from VIIRS, and BGC-Argo's fading, M33)
 * across a box 100 km wide.
 */
export const FOG_DENSITY = 0.06;
/** The water's top in a box with no depths to light (a flat floor), Phase 0's blue; the fog there fades toward it. */
const FLAT_TOP = 0x2f86b8;

const SEA_RAMP: Array<[number, number]> = [[0, 0xd9c9a0], [30, 0xb7a77c], [200, 0x6f8079], [1000, 0x3e4e5b], [4500, 0x1b2532]];
const LAND_RAMP: Array<[number, number]> = [[0, 0xcdb98a], [150, 0x7a9a52], [800, 0x5f7a45], [1600, 0x8b7d5e], [3100, 0xa39a8a]];

function ramp(stops: Array<[number, number]>, value: number, out: THREE.Color): THREE.Color {
  const first = stops[0] as [number, number];
  if (value <= first[0]) return out.setHex(first[1]);
  for (let i = 1; i < stops.length; i++) {
    const [v1, c1] = stops[i] as [number, number];
    const [v0, c0] = stops[i - 1] as [number, number];
    if (value <= v1) return out.setHex(c0).lerp(new THREE.Color(c1), (value - v0) / (v1 - v0));
  }
  return out.setHex((stops[stops.length - 1] as [number, number])[1]);
}

export class AquariumView {
  readonly group = new THREE.Group();
  /** The tagged schools marked (M156); its only animals the reef's fish. */
  readonly marks = new TagMarks();
  /** The radius of the sphere that holds the whole aquarium, land included, for fitting the camera. */
  readonly radius: number;
  /**
   * How the drawing is stretched, for the sanctuary card ("depth drawn ×75 to
   * 1,000 m, ×8 below; land ×8"), or null when the box's height is not a
   * depth. In the card rather than on the box: on a phone it lay over the
   * ruler (review M18).
   */
  readonly scaleNote: string | null = null;
  /** The projection the labels were last sized for (`fitLabels`). */
  private labelsFitFor = 0;
  private ruler: THREE.Group | null = null;
  private readonly rulerPoint = new THREE.Vector3();
  /** The camera and canvas the ruler was last decluttered for: its world matrix's sixteen, its vertical scale, the canvas's height. */
  private readonly rulerFor = new Float64Array(18).fill(Number.NaN);
  private readonly sun: THREE.Sprite;
  private readonly sunLabel: THREE.Sprite;
  /** A faint ray from the sun to the surface's middle, so the disc reads as shining on the water and not as a lamp over it. */
  private readonly ray = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, TOP, 0)]),
    new THREE.LineBasicMaterial({ color: 0xffe9a8, transparent: true, opacity: 0.35 }),
  );
  private readonly key = new THREE.DirectionalLight(0xfff4e0, KEY_AT_FULL_SUN);
  private readonly water: Water;
  /** The water's surface's skin, which `drift` slides. */
  private readonly ripple: SkinUniforms;
  /** The floor's caustics, which `setDay` lights and `drift` slides; null with no seafloor. */
  private readonly caustics: (Pick<CausticUniforms, 'uCausticDrift' | 'uCausticContrast'> & { light: THREE.BufferAttribute; depthM: Float32Array }) | null = null;
  /** The seafloor's colors as drawn, and as its elevation alone gives them, for the reef's tint (`setReef`). */
  private readonly floorColors: THREE.BufferAttribute | null = null;
  private readonly baseColors: Float32Array | null = null;
  /** The reef's fish (M83), swimming between their shelters and their feeding grounds; null with no reef. */
  private readonly fish: FishSchools | null = null;
  /** The depths' axis, for a followed school's real depth (M152); null with no seafloor. */
  private readonly axis: DepthAxis | null = null;
  /** The reef's outcrops (M87), once its model is read (`setReefModel`); null until then, and with no reef. */
  private outcrops: ReefOutcrops | null = null;
  /** What the outcrops are built from: where they may stand, and how wide they are drawn; null with no reef. */
  private readonly outcropSite: { floorKm2: number[]; floor: (column: number) => { at: THREE.Vector3; up: THREE.Vector3 }; widthUnits: number } | null = null;
  /** The reef's cover and bleaching as last set, for outcrops built after it. */
  private lastCover: { cover: readonly number[]; bleached: readonly number[]; killed: readonly number[] } | null = null;

  /**
   * @param latDeg the sanctuary's latitude, for the noon sun's path
   * @param epochDay a day of the year the path is drawn for
   */
  constructor(seafloor: Seafloor | null, private readonly reef: Reef | null, latDeg: number, epochDay: number) {
    let landTop = 0;
    let axis: DepthAxis | null = null;
    if (seafloor) {
      const summary = summarize(seafloor);
      axis = depthAxis(summary.deepestM);
      this.axis = axis;
      landTop = landShare(axis, summary.highestM) * HEIGHT;
      const floor = terrain(seafloor, axis, reef);
      this.caustics = floor.userData.caustics as NonNullable<AquariumView['caustics']>;
      this.floorColors = floor.geometry.getAttribute('color') as THREE.BufferAttribute;
      this.baseColors = Float32Array.from(this.floorColors.array as Float32Array);
      this.group.add(floor, walls(seafloor, axis), zoneLines(axis), this.marks.group);
      this.ruler = depthScale(axis);
      this.group.add(this.ruler);
      this.scaleNote = scaleNote(axis, stretches(axis, seafloor.cells * seafloor.cellKm * 1000, SIDE, HEIGHT));
      if (reef) {
        const reefOf = new Map(reef.columns.map((col, j) => [col, reef.island[j] as number]));
        // Each reef column's point, where its fish are bound, at its reef's mean depth: its floor in each depth bin,
        // at the bin's surveyed depth (a shore column's mean height can stand above the sea).
        const bins = binFloorsKm2(reef);
        const reefY = new Map(reef.columns.map((col, j) => {
          const km2 = bins.map((b) => b[j] ?? 0);
          const all = km2.reduce((a, b) => a + b, 0);
          const depth = all > 0 ? km2.reduce((sum, a, b) => sum + a * (reef.binDepthM[b] as number), 0) / all : 0;
          return [col, TOP - depthShare(axis as DepthAxis, depth) * HEIGHT];
        }));
        const onReef = (col: number, out: THREE.Vector3): THREE.Vector3 => {
          const [x, z] = columnXZ(seafloor, Math.floor(col / seafloor.cells), col % seafloor.cells);
          return out.set(x, reefY.get(col) ?? TOP, z);
        };
        // The floor's height under a point, between its columns' vertices, for the fish swimming over it.
        const positions = floor.geometry.getAttribute('position') as THREE.BufferAttribute;
        const n = seafloor.cells;
        const unit = SIDE / n;
        // Read from the vertices' own array, with nothing made per call: the fish read it some thousands of times a
        // frame, as each searches its way to its place for the shore.
        const ys = positions.array as Float32Array;
        const floorAt = (x: number, z: number): number => {
          const fk = Math.min(n - 1.000001, Math.max(0, (x + SIDE / 2) / unit - 0.5));
          const fr = Math.min(n - 1.000001, Math.max(0, (z + SIDE / 2) / unit - 0.5));
          const k = Math.floor(fk);
          const r = Math.floor(fr);
          const s = fk - k;
          const t = fr - r;
          const a = (r * n + k) * 3 + 1;
          const b = a + n * 3;
          return ((ys[a] as number) * (1 - s) + (ys[a + 3] as number) * s) * (1 - t) + ((ys[b] as number) * (1 - s) + (ys[b + 3] as number) * s) * t;
        };
        this.fish = new FishSchools(FISH_KINDS, seafloor.cells, seafloor.cellKm, reefOf, onReef, floorAt, TOP);
        this.group.add(this.fish.group);
        // An outcrop stands on the floor as drawn, at its column's vertex, and leans to its slope there.
        const normals = floor.geometry.getAttribute('normal') as THREE.BufferAttribute;
        this.outcropSite = {
          floorKm2: reef.columns.map((_, j) => bins.reduce((a, b) => a + (b[j] ?? 0), 0)),
          floor: (column) => ({ at: new THREE.Vector3().fromBufferAttribute(positions, column), up: new THREE.Vector3().fromBufferAttribute(normals, column).normalize() }),
          widthUnits: OUTCROP_UNITS,
        };
      }
    } else {
      this.group.add(flatFloor());
    }
    this.water = waterBody(axis);
    const pane = surface();
    this.ripple = pane.skin;
    this.group.add(this.water.mesh, glassEdges(), pane.mesh, openOcean());
    this.group.add(compass());
    const path = yearOfNoons(latDeg, epochDay);
    this.sun = sunDisc();
    this.sunLabel = label('noon sun', 11, '#ffe9a8');
    // Hung below the disc: its anchor above the label by the disc's half and a little.
    this.sunLabel.center.set(0.5, 1 + (SUN_PX / 2 + 2) / (this.sunLabel.userData.labelPx as number));
    this.group.add(sunPath(path.fromDeg, path.toDeg), this.sun, this.sunLabel, this.ray, this.key);
    // The sphere holds the box, the land and the sun wherever the year puts it.
    let sky = 0;
    for (let m = path.fromDeg; m <= path.toDeg; m += 1) sky = Math.max(sky, sunAt(m).length());
    this.radius = Math.max(Math.hypot(SIDE / 2, SIDE / 2, HEIGHT / 2 + landTop), sky + SUN_MARGIN);
  }

  /**
   * Draws a day's sun (`daySun`): the disc where it stands at noon, the key
   * light from there at the day's strength, and the water's glow for the
   * day's clarity. Cheap enough for every day at the fastest speed: a few
   * thousand vertices' light.
   */
  setDay(day: DaySun): void {
    const up = day.meridianDeg !== null;
    this.sun.visible = up;
    this.sunLabel.visible = up;
    this.ray.visible = up;
    this.key.intensity = KEY_AT_FULL_SUN * day.beam;
    if (day.meridianDeg !== null) {
      const at = sunAt(day.meridianDeg);
      this.sun.position.copy(at);
      this.sunLabel.position.copy(at);
      // Along the sun's direction from the origin, where three.js aims the light (`sunDirection`).
      this.key.position.set(...sunDirection(day.meridianDeg));
      const ends = this.ray.geometry.getAttribute('position') as THREE.BufferAttribute;
      ends.setXYZ(0, at.x, at.y, at.z);
      ends.needsUpdate = true;
      // The bounds three.js culls by are found once; the ray's end has moved (review M36: off the
      // first day's bounds, the ray vanished from some views while its sun was on screen).
      this.ray.geometry.computeBoundingSphere();
      // A dim day's disc is paler, never gone (JUDGED).
      (this.sun.material as THREE.SpriteMaterial).opacity = 0.45 + 0.55 * Math.min(1, day.beam);
    }
    this.water.glow(day.kdPar, Math.min(1, day.beam));
    if (this.caustics) {
      // Each wet vertex's share of the day's beam at its depth, by the measured clarity; none on land.
      const { light, depthM } = this.caustics;
      const strength = Math.min(1, day.beam);
      const shares = light.array as Float32Array;
      for (let i = 0; i < depthM.length; i++) {
        const z = depthM[i] as number;
        shares[i] = z > 0 ? strength * lightShare(day.kdPar, z) : 0;
      }
      light.needsUpdate = true;
    }
  }

  /**
   * Tints the reef's columns for the day's coral: each toward live coral's
   * color by its floor within the reef's depths (`FULL_REEF_KM2`), more
   * with its cover against the ceiling's, then toward white by the share
   * of its coral bleached. A few hundred vertices; skipped where the box has
   * no reef.
   */
  setReef(columnCoverPercent: readonly number[], columnBleachedPercent: readonly number[], columnKilledPercent: readonly number[] = []): void {
    if (!this.reef || !this.floorColors || !this.baseColors) return;
    const colors = this.floorColors.array as Float32Array;
    const c = new THREE.Color();
    const shares = reefShares(this.reef);
    this.reef.columns.forEach((col, j) => {
      const reef = FULL_REEF_TINT * (shares[j] ?? 0);
      // Drawn as fully coral at the reef's ceiling (review M50: it was written here again, rounded).
      const live = Math.min(1, (columnCoverPercent[j] ?? 0) / CORAL_RATES.ceilingPercent);
      c.setRGB(this.baseColors?.[col * 3] ?? 0, this.baseColors?.[col * 3 + 1] ?? 0, this.baseColors?.[col * 3 + 2] ?? 0)
        .lerp(LIVE_CORAL, reef * (BARE_REEF_TINT + (1 - BARE_REEF_TINT) * live))
        .lerp(BLEACHED_CORAL, reef * Math.min(1, (columnBleachedPercent[j] ?? 0) / 100));
      colors.set([c.r, c.g, c.b], col * 3);
    });
    this.floorColors.needsUpdate = true;
    this.lastCover = { cover: columnCoverPercent, bleached: columnBleachedPercent, killed: columnKilledPercent };
    this.outcrops?.setCover(columnCoverPercent, columnBleachedPercent, columnKilledPercent);
  }

  /**
   * The reef's outcrops (M87) from its model, read once a page (`main.ts`),
   * with their skins when the browser has read them; nothing where the box
   * has no reef. A view built before the model arrives gets them when it
   * does.
   */
  setReefModel(model: ReefModel, textures?: ReefTextures): void {
    if (!this.reef || !this.outcropSite || this.outcrops) return;
    const { floorKm2, floor, widthUnits } = this.outcropSite;
    this.outcrops = new ReefOutcrops(model, outcropPlaces(this.reef, floorKm2, floor, widthUnits, model.outcrops.length, TOP), widthUnits);
    if (textures) this.outcrops.setTextures(textures);
    if (this.lastCover) this.outcrops.setCover(this.lastCover.cover, this.lastCover.bleached, this.lastCover.killed);
    this.group.add(this.outcrops.group);
  }

  /**
   * Slides the ripple on by `seconds` of the player's time
   * (`RIPPLE_DRIFT_PER_S`), across its crests, and the caustics' two webs
   * across each other (`CAUSTIC_DRIFT_PER_S`); each kept within a tile, where
   * the pattern repeats, so the offset never grows past single precision's
   * reach.
   */
  drift(seconds: number): void {
    const d = this.ripple.uSkinDrift.value;
    // The ripple's own tile repeats every one of its heights.
    d.y = (d.y + seconds * RIPPLE_DRIFT_PER_S) % (SKIN_TEXEL * RIPPLE_TILE.height);
    if (this.caustics) {
      const c = this.caustics.uCausticDrift.value;
      const step = seconds * CAUSTIC_DRIFT_PER_S;
      const [[ax, ay], [bx, by]] = CAUSTIC_DIRECTIONS;
      // Wrapped up from below too, the second web's x running backward.
      // Each within the drawn web's tile, where it repeats.
      const tile = SKIN_TEXEL * SKIN_TILE_PX;
      const wrapTile = (v: number): number => ((v % tile) + tile) % tile;
      c.set(wrapTile(c.x + ax * step), wrapTile(c.y + ay * step), wrapTile(c.z + bx * step), wrapTile(c.w + by * step));
    }
  }

  /** The reef's fish on a day, each kind's in `FISH_KINDS` order (M83). */
  setFish(counts: readonly KindCounts[]): void {
    this.fish?.setCounts(counts);
  }

  /** The school of the fish drawn nearest a point on the screen, by its seed; -1 for none (M152: the map's fish followed as the tank's). */
  pickSchool(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return this.fish?.pick(camera, x, y, widthPx, heightPx, withinPx) ?? -1;
  }

  /** A school as the wildlife card tells it, by its seed, with its fish's real depth, m, by the map's depth axis; null for none. */
  schoolAt(seed: number): (NonNullable<ReturnType<FishSchools['schoolAt']>> & { species: (typeof FISH_KINDS)[number]['species']; depthM: number; spritesOfKind: number; kindPerSprite: number; level: number }) | null {
    const s = this.fish?.schoolAt(seed);
    if (!s || !this.fish || !this.axis) return null;
    const kind = FISH_KINDS[s.kind] as (typeof FISH_KINDS)[number];
    return { ...s, species: kind.species, depthM: depthAtShare(this.axis, (TOP - s.middle.y) / HEIGHT), spritesOfKind: this.fish.spritesOf(s.kind), kindPerSprite: kind.perSprite ?? FISH_PER_SPRITE, level: this.fish.level() };
  }

  /**
   * The reef's fish where their day has them at `hour` (solar hours since midnight) of a day `dayLengthHours`
   * long (M83), their schools moved on by `seconds` of swimming (M88), still within themselves when `calm`.
   */
  swimFish(hour: number, dayLengthHours: number, seconds: number, calm = false): void {
    this.fish?.swim(hour, dayLengthHours, seconds, calm);
  }

  /**
   * Sizes every label to its pixel height on a canvas `canvasPx` CSS pixels
   * high: with no size attenuation a sprite of height h covers h times the
   * projection's vertical scale of the screen's two NDC units. Cheap, and
   * skipped when nothing that sets it has changed. The fish are sized with
   * them.
   */
  fitLabels(camera: THREE.PerspectiveCamera, canvasPx: number, seconds = Infinity): void {
    // The seconds since the last, so a fish's turn is held (`TURN_HOLD_S`; M159's review: given none, the map's fish
    // turned back within a second 981 times in 20 s among 541).
    this.fish?.fit(camera, 0, 0, seconds);
    const p11 = camera.projectionMatrix.elements[5] ?? 1;
    const key = p11 * canvasPx;
    if (key !== this.labelsFitFor) {
      this.labelsFitFor = key;
      this.group.traverse((o) => {
        if (!(o instanceof THREE.Sprite) || typeof o.userData.labelPx !== 'number') return;
        const h = (2 * o.userData.labelPx) / key;
        o.scale.set(h * (o.userData.aspect as number), h, 1);
      });
    }
    this.declutterRuler(camera, canvasPx);
  }

  /**
   * Hides the depth labels that would overlap on screen (`placeRulerLabels`).
   * A projection a label each frame while the camera moves (ten at Maui
   * Nui), because turning it changes how far apart the ticks are; none while
   * it is still.
   */
  private declutterRuler(camera: THREE.PerspectiveCamera, canvasPx: number): void {
    if (!this.ruler) return;
    // Nothing to do while the camera and the canvas are as they were (review M36: it ran every frame).
    const e = camera.matrixWorld.elements;
    const p11 = camera.projectionMatrix.elements[5] ?? 1;
    let same = this.rulerFor[16] === p11 && this.rulerFor[17] === canvasPx;
    for (let i = 0; i < 16 && same; i++) same = e[i] === this.rulerFor[i];
    if (same) return;
    this.rulerFor.set(e);
    this.rulerFor[16] = p11;
    this.rulerFor[17] = canvasPx;
    const sprites = this.ruler.children.filter((o): o is THREE.Sprite => o instanceof THREE.Sprite && typeof o.userData.depthM === 'number');
    const shown = placeRulerLabels(sprites.map((o) => {
      this.rulerPoint.copy(o.position).project(camera);
      return {
        depthM: o.userData.depthM as number,
        screenY: ((1 - this.rulerPoint.y) / 2) * canvasPx,
        heightPx: o.userData.labelPx as number,
        keep: o.userData.keep === true,
      };
    }));
    sprites.forEach((o, i) => { o.visible = shown[i] ?? true; });
  }

  /** Where a tag's mark stands (M156): over a school of its kind on its island; null for the rest, none drawn here. */
  markAt(target: MarkTarget, remembered: number | null): THREE.Vector3 | null {
    if (!('island' in target) || !this.fish) return null;
    const kind = FISH_KINDS.findIndex((k) => k.species === target.species);
    return kind < 0 ? null : schoolMark(this.fish, kind, target.island, remembered);
  }

  dispose(): void {
    // The fish's and the outcrops' own first (M83's review: their textures, kept by their layers, were never freed).
    this.fish?.dispose();
    if (this.outcrops) {
      this.group.remove(this.outcrops.group);
      this.outcrops.dispose();
    }
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Line || o instanceof THREE.Sprite) {
        // Every sprite shares three.js's one quad (review M36: disposed with a view, it was uploaded again under the next).
        if (!(o instanceof THREE.Sprite)) o.geometry.dispose();
        const m = o.material as THREE.Material & { map?: THREE.Texture | null };
        m.map?.dispose();
        m.dispose();
      }
    });
  }
}

/** The scene height of an elevation, m: below the surface on the depth axis, above it at the land's scale. */
function heightOf(axis: DepthAxis, elevation: number): number {
  return elevation >= 0 ? TOP + landShare(axis, elevation) * HEIGHT : TOP - depthShare(axis, -elevation) * HEIGHT;
}

/** Scene x and z of a column's center: column 0 the western edge, row 0 the northern (away from the default camera). */
function columnXZ(seafloor: Seafloor, row: number, col: number): [number, number] {
  const unit = SIDE / seafloor.cells;
  return [(col + 0.5) * unit - SIDE / 2, (row + 0.5) * unit - SIDE / 2];
}

/** Each reef column's share of the floor drawn as reef, 0 to 1, in `reef.columns`' order: its floor to the reef's depth against `FULL_REEF_KM2`. */
function reefShares(reef: Reef): number[] {
  const bins = binFloorsKm2(reef);
  return reef.columns.map((_, j) => Math.min(1, bins.reduce((a, b) => a + (b[j] ?? 0), 0) / FULL_REEF_KM2));
}

/**
 * The seafloor and the land as one height field through the columns' centers,
 * skinned: each vertex carries its share of land (1 above the sea) and of
 * reef, which choose its tiles (`skin.ts`).
 */
function terrain(seafloor: Seafloor, axis: DepthAxis, reef: Reef | null): THREE.Mesh {
  const n = seafloor.cells;
  const positions = new Float32Array(n * n * 3);
  const colors = new Float32Array(n * n * 3);
  const land = new Float32Array(n * n);
  const depthM = new Float32Array(n * n);
  const reefShare = new Float32Array(n * n);
  if (reef) {
    const shares = reefShares(reef);
    reef.columns.forEach((col, j) => { reefShare[col] = shares[j] ?? 0; });
  }
  const c = new THREE.Color();
  for (let r = 0; r < n; r++) {
    for (let k = 0; k < n; k++) {
      const i = r * n + k;
      const e = elevationM(seafloor, i);
      const [x, z] = columnXZ(seafloor, r, k);
      positions.set([x, heightOf(axis, e), z], i * 3);
      land[i] = e >= 0 ? 1 : 0;
      depthM[i] = Math.max(0, -e);
      if (e >= 0) ramp(LAND_RAMP, e, c);
      else ramp(SEA_RAMP, -e, c);
      colors.set([c.r, c.g, c.b], i * 3);
    }
  }
  const index: number[] = [];
  for (let r = 0; r < n - 1; r++) {
    for (let k = 0; k < n - 1; k++) {
      const a = r * n + k;
      index.push(a, a + n, a + 1, a + 1, a + n, a + n + 1);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('aSkinLand', new THREE.BufferAttribute(land, 1));
  geometry.setAttribute('aSkinReef', new THREE.BufferAttribute(reefShare, 1));
  // The sun's share at each vertex's depth, written each day (`setDay`); dark until the first.
  const light = new THREE.BufferAttribute(new Float32Array(n * n), 1);
  geometry.setAttribute('aCaustic', light);
  geometry.setIndex(index);
  geometry.computeVertexNormals();
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });
  const skin = attachSkin(material, { texel: SKIN_TEXEL, caustics: true, fog: true, floor: { sand: sandTile() } });
  setFog(skin, UNLIT_TOP);
  if (skin.uCausticContrast) skin.uCausticContrast.value = CAUSTIC_CONTRAST;
  if (skin.uCausticSurface) skin.uCausticSurface.value = TOP;
  const mesh = new THREE.Mesh(geometry, material);
  mesh.userData.caustics = { uCausticDrift: skin.uCausticDrift, uCausticContrast: skin.uCausticContrast, light, depthM };
  return mesh;
}

/** Fogs a surface seen through the water: the water's box, its density (`FOG_DENSITY`), and its color, the water's own near its top. */
function setFog(skin: Partial<FogUniforms>, color: number): void {
  if (!skin.uFogBoxMin || !skin.uFogBoxMax || !skin.uFogDensity || !skin.uFogColor) throw new Error('a fogged surface without its fog');
  skin.uFogBoxMin.value.set(-SIDE / 2, BOTTOM, -SIDE / 2);
  skin.uFogBoxMax.value.set(SIDE / 2, TOP, SIDE / 2);
  skin.uFogDensity.value = FOG_DENSITY;
  skin.uFogColor.value.setHex(color);
  // The map's fog one color at every depth (the exhibit's darkens, M94).
  skin.uFogDeep?.value.setHex(color);
}

/** The cutaway at the four walls: from the seafloor's edge down to the box's floor. */
function walls(seafloor: Seafloor, axis: DepthAxis): THREE.Mesh {
  const n = seafloor.cells;
  const positions: number[] = [];
  const colors: number[] = [];
  const rock = new THREE.Color(0x4a4038);
  const deep = new THREE.Color(0x231e1a);
  const edges: Array<Array<[number, number]>> = [
    Array.from({ length: n }, (_, k) => [0, k]),
    Array.from({ length: n }, (_, k) => [n - 1, k]),
    Array.from({ length: n }, (_, r) => [r, 0]),
    Array.from({ length: n }, (_, r) => [r, n - 1]),
  ];
  for (const edge of edges) {
    for (let j = 0; j < edge.length - 1; j++) {
      const [r0, k0] = edge[j] as [number, number];
      const [r1, k1] = edge[j + 1] as [number, number];
      const [x0, z0] = columnXZ(seafloor, r0, k0);
      const [x1, z1] = columnXZ(seafloor, r1, k1);
      const y0 = heightOf(axis, elevationM(seafloor, r0 * n + k0));
      const y1 = heightOf(axis, elevationM(seafloor, r1 * n + k1));
      positions.push(x0, y0, z0, x0, BOTTOM, z0, x1, y1, z1, x1, y1, z1, x0, BOTTOM, z0, x1, BOTTOM, z1);
      for (const t of [rock, deep, rock, rock, deep, deep]) colors.push(t.r, t.g, t.b);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  const material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
  setFog(attachSkin(material, { texel: SKIN_TEXEL, only: 'rock', fog: true }), UNLIT_TOP);
  return new THREE.Mesh(geometry, material);
}

/** The water, and how to light it for a day. */
interface Water {
  mesh: THREE.Mesh;
  /** Glows the water for a clarity, m⁻¹, at a share of full strength. */
  glow(kdPar: number, strength: number): void;
}

/**
 * The water in the glass, in rows, shaded by height from a dark blue at the
 * top to near-black at the floor, and lit toward a sunlit blue as far down as
 * the day's light reaches, its walls more opaque there so the lit band reads
 * from the side; the surface keeps its opacity, so the seafloor shows from
 * above. A box without a depth axis (a flat floor) has no depths to light: it
 * keeps Phase 0's blue at the top.
 */
function waterBody(axis: DepthAxis | null): Water {
  const geometry = new THREE.BoxGeometry(SIDE * 0.995, HEIGHT * 0.995, SIDE * 0.995, 1, axis ? WATER_ROWS : 1, 1);
  const top = new THREE.Color(axis ? UNLIT_TOP : FLAT_TOP);
  const bottom = new THREE.Color(UNLIT_BOTTOM);
  const lit = new THREE.Color(LIT);
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const base = new Float32Array(position.count * 4);
  // Each vertex's row, and each row's depth: the glow is worked out once a row (review M36: once a
  // vertex, 2,416 of them on 301 rows, was eight times the work).
  const rowOf = new Int32Array(position.count);
  const depths: number[] = [];
  const rows = new Map<number, number>();
  const c = new THREE.Color();
  for (let i = 0; i < position.count; i++) {
    const t = (position.getY(i) + HEIGHT / 2) / HEIGHT;
    c.copy(bottom).lerp(top, t);
    base.set([c.r, c.g, c.b, WATER_OPACITY], i * 4);
    let row = rows.get(position.getY(i));
    if (row === undefined) {
      row = depths.length;
      rows.set(position.getY(i), row);
      depths.push(axis ? depthAtShare(axis, 1 - t) : 0);
    }
    rowOf[i] = row;
  }
  const glowOfRow = new Float64Array(depths.length);
  const colors = new THREE.BufferAttribute(base.slice(), 4);
  geometry.setAttribute('color', colors);
  const material = new THREE.MeshBasicMaterial({
    vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = 1;
  const glow = (kdPar: number, strength: number): void => {
    if (!axis) return;
    depths.forEach((d, row) => { glowOfRow[row] = glowAt(kdPar, d) * strength; });
    for (let i = 0; i < position.count; i++) {
      const g = glowOfRow[rowOf[i] as number] as number;
      c.setRGB(base[i * 4] as number, base[i * 4 + 1] as number, base[i * 4 + 2] as number).lerp(lit, g);
      // A wall's normal is level; the surface's and the floor's are not.
      const wall = Math.abs(normal.getY(i)) < 0.5;
      colors.setXYZW(i, c.r, c.g, c.b, WATER_OPACITY + (wall ? LIT_OPACITY * g : 0));
    }
    colors.needsUpdate = true;
  };
  return { mesh, glow };
}

/** Where the sun's disc is drawn at a meridian angle (`meridianDeg`): on the path's half circle over the surface's middle. */
function sunAt(meridian: number): THREE.Vector3 {
  return new THREE.Vector3(...sunDirection(meridian)).multiplyScalar(SUN_PATH_RADIUS).add(new THREE.Vector3(0, TOP, 0));
}

/** The noon sun's path over the year, a dashed arc from its lowest noon to its highest and on. */
function sunPath(fromDeg: number, toDeg: number): THREE.Line {
  const points: THREE.Vector3[] = [];
  for (let m = fromDeg; m < toDeg; m += 1) points.push(sunAt(m));
  points.push(sunAt(toDeg));
  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.LineDashedMaterial({ color: 0xffe9a8, transparent: true, opacity: 0.5, dashSize: 0.18, gapSize: 0.14 }),
  );
  line.computeLineDistances();
  return line;
}

/** The sun's disc: a warm white core in a soft glow, `SUN_PX` across on screen. */
function sunDisc(): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, 'rgba(255,253,240,1)');
  g.addColorStop(0.32, 'rgba(255,240,190,1)');
  g.addColorStop(0.42, 'rgba(255,214,120,0.55)');
  g.addColorStop(1, 'rgba(255,200,90,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, sizeAttenuation: false, depthWrite: false }));
  sprite.userData.labelPx = SUN_PX;
  sprite.userData.aspect = 1;
  sprite.renderOrder = 4;
  return sprite;
}

function glassEdges(): THREE.LineSegments {
  const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(SIDE, HEIGHT, SIDE));
  return new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0xbfe6ff, transparent: true, opacity: 0.8 }));
}

function flatFloor(): THREE.Mesh {
  const material = new THREE.MeshStandardMaterial({ color: 0xb8a77f, roughness: 1 });
  setFog(attachSkin(material, { texel: SKIN_TEXEL, own: sandTile(), fog: true }), FLAT_TOP);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(SIDE * 0.99, SIDE * 0.99), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = BOTTOM + 0.01;
  return mesh;
}

/** The user's ripple as a texture, made once a page: every view a load builds shares it (`dispose` frees only a material's `map`). */
let ripple: OwnTile | null = null;
const rippleTile = (): OwnTile => (ripple ??= ownTile(RIPPLE_TILE));
/** The user's sand as a texture, made once a page, as the ripple's is. */
let sand: OwnTile | null = null;
const sandTile = (): OwnTile => (sand ??= ownTile(SEAFLOOR_TILE));

/** The water's surface: a faint pane, rippled (M77, the user's word), its ripple laid on its color and its opacity, and its skin to drift. */
function surface(): { mesh: THREE.Mesh; skin: SkinUniforms } {
  const material = new THREE.MeshBasicMaterial({ color: 0x8fd3ff, transparent: true, opacity: 0.14, side: THREE.DoubleSide, depthWrite: false });
  const skin = attachSkin(material, { texel: SKIN_TEXEL, own: rippleTile(), alpha: true });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(SIDE * 0.99, SIDE * 0.99), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = TOP - 0.01;
  mesh.renderOrder = 2;
  return { mesh, skin };
}

/** How far the open ocean runs from the box's middle, scene units (JUDGED); its rim is hidden by its fade, not by the camera's farthest (M81's reviewer: that is 72 at Maui Nui on a landscape screen, about 117 on an upright phone). */
const OCEAN_RADIUS = SIDE * 8;
/** The open ocean's opacity at the box's edge, fading to none at its rim (JUDGED: the cutaway's walls read through it). */
const OCEAN_OPACITY = 0.14;

/**
 * The open ocean around the box (the user's word: "i want the feeling of an
 * expansive aquarium", "the open ocean beyond"): the surface carried on past
 * the glass to the horizon, plain water (its ripple taken off at the user's
 * word: "it looks terrible. get rid of the ripples on the open ocean"),
 * fading with distance into the night around it. Translucent, so the
 * cutaway's walls, under it from every view above the water, still read
 * through it.
 */
function openOcean(): THREE.Mesh {
  const material = new THREE.MeshBasicMaterial({ color: 0x3f8fbf, transparent: true, opacity: OCEAN_OPACITY, side: THREE.DoubleSide, depthWrite: false });
  // Faded by the distance from the box's middle, in the disc's own plane.
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vOcean;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvOcean = position.xy;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vOcean;')
      .replace('#include <opaque_fragment>',
        `diffuseColor.a *= 1.0 - smoothstep(${(SIDE / 2).toFixed(1)}, ${OCEAN_RADIUS.toFixed(1)}, length(vOcean));\n#include <opaque_fragment>`);
  };
  material.customProgramCacheKey = () => 'ocean';
  // A disc with the box's surface cut out of it, edge to edge with the surface's pane.
  const half = (SIDE * 0.99) / 2;
  const shape = new THREE.Shape().absarc(0, 0, OCEAN_RADIUS, 0, Math.PI * 2, false);
  shape.holes.push(new THREE.Path([new THREE.Vector2(-half, -half), new THREE.Vector2(half, -half), new THREE.Vector2(half, half), new THREE.Vector2(-half, half)]));
  const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape, 96), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = TOP - 0.01;
  mesh.renderOrder = 2;
  // Not under the pointer: a zoom aims at the box, not at water far past it (M81's reviewer: a hit on the disc near its
  // rim, faded to nothing, set the zoom's pivot 119 units off).
  mesh.raycast = () => undefined;
  return mesh;
}

/** The canvas text is drawn this many pixels high, inside a margin of 8 on every side. */
const LABEL_FONT_PX = 48;
const LABEL_MARGIN_PX = 8;

/**
 * A text label that always faces the camera and keeps one size on screen,
 * `cssPx` high (its letters' font size), whatever the screen and however far
 * the camera stands (review M18: sized in scene units, the ruler's letters
 * were about 4 px tall on a 320 px phone). `AquariumView.fitLabels` sets the
 * size whenever the projection changes.
 */
export function label(text: string, cssPx: number, color = '#dff3ff'): THREE.Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  ctx.font = `600 ${LABEL_FONT_PX}px system-ui, sans-serif`;
  canvas.width = Math.ceil(ctx.measureText(text).width) + 2 * LABEL_MARGIN_PX;
  canvas.height = LABEL_FONT_PX + 2 * LABEL_MARGIN_PX;
  ctx.font = `600 ${LABEL_FONT_PX}px system-ui, sans-serif`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.85)';
  ctx.shadowBlur = 6;
  ctx.fillText(text, LABEL_MARGIN_PX, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true, sizeAttenuation: false });
  const sprite = new THREE.Sprite(material);
  sprite.userData.text = text;
  sprite.userData.labelPx = (cssPx * canvas.height) / LABEL_FONT_PX;
  sprite.userData.aspect = canvas.width / canvas.height;
  sprite.renderOrder = 5;
  return sprite;
}

/** The boundaries between the ocean's layers, as faint lines around the glass. */
function zoneLines(axis: DepthAxis): THREE.LineSegments {
  const points: number[] = [];
  const h = SIDE / 2;
  for (const z of DEPTH_ZONES) {
    if (z.topM <= 0 || z.topM >= axis.deepestM) continue;
    const y = TOP - depthShare(axis, z.topM) * HEIGHT;
    const corners = [[-h, -h], [h, -h], [h, h], [-h, h], [-h, -h]];
    for (let i = 0; i < 4; i++) {
      const [x0, z0] = corners[i] as number[];
      const [x1, z1] = corners[i + 1] as number[];
      points.push(x0 as number, y, z0 as number, x1 as number, y, z1 as number);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  return new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: 0xbfe6ff, transparent: true, opacity: 0.35 }));
}

/**
 * The true-depth ruler on the corner nearest the default camera, in real
 * meters: every 200 m to the break, every 1,000 m below it, and the deepest
 * water at the bottom, labeled to the right; each layer named to the left, in
 * the middle of its band where the band is tall enough to hold its name. On
 * that corner, no panel covers the map's at any of the layout probe's screen
 * shapes.
 */
function depthScale(axis: DepthAxis): THREE.Group {
  const group = new THREE.Group();
  const x = SIDE / 2 + 0.2;
  const z = SIDE / 2 + 0.2;
  const yOf = (m: number): number => TOP - depthShare(axis, m) * HEIGHT;
  const points: number[] = [x, TOP, z, x, BOTTOM, z];
  const ticks: number[] = [];
  for (let m = 0; m <= axis.breakM; m += 200) ticks.push(m);
  for (let m = 2000; m < axis.deepestM; m += 1000) ticks.push(m);
  for (const m of ticks) {
    const y = yOf(m);
    points.push(x, y, z, x + 0.25, y, z);
    const tick = label(`${m.toLocaleString('en-US')} m`, 11);
    tick.center.set(0, 0.5);
    tick.position.set(x + 0.35, y, z);
    tick.userData.depthM = m;
    tick.userData.keep = m === 0 || m === axis.breakM;
    group.add(tick);
  }
  const floor = label(`${Math.round(axis.deepestM).toLocaleString('en-US')} m`, 11, '#9fd6ff');
  floor.center.set(0, 0.5);
  floor.position.set(x + 0.35, BOTTOM, z);
  floor.userData.depthM = axis.deepestM;
  floor.userData.keep = true;
  group.add(floor);
  for (const zone of DEPTH_ZONES) {
    if (zone.topM >= axis.deepestM) continue;
    const top = yOf(zone.topM);
    const bottom = yOf(Math.min(zone.bottomM, axis.deepestM));
    if (top - bottom < 0.8) continue;
    const name = label(zone.zone, 11, '#a9cbe0');
    name.center.set(1, 0.5);
    name.position.set(x - 0.15, (top + bottom) / 2, z);
    group.add(name);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  group.add(new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: 0xdff3ff })));
  return group;
}

/** The stretches in words, for the sanctuary card. */
function scaleNote(axis: DepthAxis, stretch: { upper: number; lower: number }): string {
  if (axis.upperShare >= 1) return `depth and land ×${Math.round(stretch.upper)}`;
  return `depth ×${Math.round(stretch.upper)} to ${axis.breakM.toLocaleString('en-US')} m and `
    + `×${Math.round(stretch.lower)} below; land ×${Math.round(stretch.lower)}`;
}

/** North, at the far edge. */
function compass(): THREE.Sprite {
  const n = label('N', 14, '#ffe9a8');
  n.center.set(0.5, 0.5);
  n.position.set(0, TOP + 0.4, -SIDE / 2 - 0.5);
  return n;
}
